import * as ko from 'knockout';

import i18n from '../i18n';
import { downloadBlob } from '../utils';
import { extractServerErrorMessages, uploadFile } from '../api/request';
import { logError } from '../error_logging';
import { asObservable } from '../utils/ko_utils';
import { goBackOrHome } from '../utils/navigation';
import { FileUploadDelegate } from './basic_widgets';

let template = require('raw-loader!../../templates/components/import_entities.html').default;

export interface ImportEntitiesDelegate<T = {}> {
  title: ko.Computed<string> | ko.Observable<string>;
  description: string;
  backTitle: string;
  loading?: KnockoutObservable<boolean>;
  downloadTemplateEnabled?: KnockoutSubscribable<boolean>;
  links?: { href: string; title: string }[];

  templateBaseName: string | ko.Observable<string>;
  downloadTemplate: () => Promise<Blob>;
  importUrl: string | (() => string);

  prepareFileContents?: (fileContents: ArrayBuffer) => ArrayBuffer | string;
  onSuccess?: (data: T) => Promise<ImportError[]>;

  // When `enabled` is true, the upload is handed off to `resolve` instead of the default PUT.
  // `resolve` runs its own multi-step flow (e.g. validate + interactive conflict resolution +
  // commit) and reports the final outcome, or null if the user cancelled. ``data`` carries the
  // imported records so it can be passed to ``onSuccess`` exactly like the default PUT path.
  manualResolution?: {
    enabled: KnockoutSubscribable<boolean>;
    resolve: (fileContents: ArrayBuffer) => Promise<ManualResolutionOutcome<T> | null>;
  };

  // A sentence the delegate's `onSuccess` sets to describe what the import did beyond the
  // imported records themselves (e.g. how many were attached to a trial). Shown under the result
  // message, including when no record was added or updated. Cleared before each upload.
  resultNote?: KnockoutObservable<string | null>;
}

export interface ManualResolutionOutcome<T = {}> {
  data: T;
  errors: ImportError[];
  added_count: number;
  updated_count: number;
}

type ImportState = 'initial' | 'preparing' | 'importing' | 'imported';

// The api layer rejects with an Error for transport failures and with null for an error response.
function describeRejection(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.message || reason.name;
  }
  return reason === null || reason === undefined ? 'no reason given' : String(reason);
}

export interface ImportError {
  sheet: string;
  cell: string;
  message: string;
}

export interface PartialMatchInfo {
  sheet: string;
  missing_headers_in_sheet: string[];
  unknown_headers_in_sheet: string[];
}

class ImportEntities implements FileUploadDelegate {
  private delegate: KnockoutObservable<ImportEntitiesDelegate>;

  private defaultError = i18n.t(
    "Couldn't upload Excel file. Please check your internet connection and try again."
  )();
  preparingText = i18n.t('Preparing Excel template...');
  importingText = i18n.t('Importing Excel file...');

  loading = ko.pureComputed(() => (this.delegate().loading ? this.delegate().loading() : false));

  state = ko.observable<ImportState>('initial');
  errors = ko.observableArray<ImportError>();
  partialMatchInfo = ko.observableArray<PartialMatchInfo>();
  excelUploadError = ko.observable('');

  addedCount = ko.observable<number | null>(null);
  updatedCount = ko.observable<number | null>(null);

  hasCounts = ko.pureComputed(() => this.addedCount() !== null || this.updatedCount() !== null);

  hasNoChanges = ko.pureComputed(
    () => this.hasCounts() && (this.addedCount() ?? 0) === 0 && (this.updatedCount() ?? 0) === 0
  );

  resultNote = ko.pureComputed(() => this.delegate().resultNote?.() ?? null);

  importResultMessage = ko.pureComputed(() => {
    if (!this.hasCounts()) {
      return i18n.t('Excel file successfully imported.')();
    }
    return i18n.t('Import completed. Added {{added}} records, updated {{updated}} records.', {
      added: this.addedCount() ?? 0,
      updated: this.updatedCount() ?? 0,
    })();
  });

  downloadTemplateEnabled = ko.pureComputed(() => {
    return this.delegate().downloadTemplateEnabled?.() ?? true;
  });

  constructor(
    params: { delegate: KnockoutObservable<ImportEntitiesDelegate> },
    componentInfo: KnockoutComponentTypes.ComponentInfo
  ) {
    this.delegate = asObservable(params.delegate);
  }

  dispose() { }

  goBack = () => {
    goBackOrHome();
  };

  // Bare-list 400 bodies are the workbook-level errors these import endpoints raise;
  // server messages arrive already localized.
  private errorMessageFromXHR = (xhr: JQuery.jqXHR | undefined): string => {
    if (
      xhr?.status === 400 &&
      Array.isArray(xhr.responseJSON) &&
      xhr.responseJSON.every((item: unknown) => typeof item === 'string')
    ) {
      return xhr.responseJSON.join(' ');
    }
    const messages = extractServerErrorMessages(xhr?.responseJSON);
    return messages.length > 0 ? messages.join(' ') : this.defaultError;
  };

  downloadTemplate = () => {
    if (!this.downloadTemplateEnabled()) {
      return;
    }

    this.state('preparing');

    this.delegate()
      .downloadTemplate()
      .then((data) => {
        this.state('initial');
        const templateName = ko.unwrap(this.delegate().templateBaseName);
        downloadBlob(data, `${templateName} - ${new Date().toLocaleString()}.xlsx`);
      })
      .catch(() => {
        this.state('initial');
      });
  };

  onFileContents = (
    userFileName: string,
    fileContents: ArrayBuffer,
    contentType: string,
    prepareXHR: () => XMLHttpRequest
  ) => {
    this.excelUploadError('');
    this.errors([]);
    this.partialMatchInfo([]);
    this.addedCount(null);
    this.updatedCount(null);
    this.delegate().resultNote?.(null);

    let delegate = this.delegate();
    let data: ArrayBuffer | string = fileContents;
    if (delegate.prepareFileContents) {
      data = delegate.prepareFileContents(fileContents);
    }

    const manualResolution = delegate.manualResolution;
    if (manualResolution && manualResolution.enabled()) {
      this.state('importing');
      manualResolution
        .resolve(fileContents)
        .then(async (outcome) => {
          if (outcome === null) {
            this.state('initial'); // the user cancelled the conflict dialog
            return;
          }
          await this.applyImportOutcome(outcome);
        })
        .catch((xhr) => {
          this.state('initial');
          this.excelUploadError(this.errorMessageFromXHR(xhr));
        });
      return;
    }

    const delegateImportUrl = this.delegate().importUrl;
    const importUrl = typeof delegateImportUrl === 'string' ? delegateImportUrl : delegateImportUrl();

    uploadFile<{
      data: {};
      errors: ImportError[];
      partial_match_info?: PartialMatchInfo[];
      added_count?: number;
      updated_count?: number;
    }>(importUrl, data, {
      headers: { 'Content-Disposition': 'attachment; filename="file"' },
      xhr: () => {
        let xhr = prepareXHR();
        xhr.upload.addEventListener('load', (event: ProgressEvent) => {
          let percent = Math.ceil((event.loaded / event.total) * 100);

          if (percent >= 100) {
            this.state('importing');
          }
        });

        return xhr;
      },
    })
      .then(async (res) => {
        this.partialMatchInfo(res.partial_match_info ?? []);
        await this.applyImportOutcome(res);
      })
      .catch((xhr) => {
        this.state('initial');
        this.excelUploadError(this.errorMessageFromXHR(xhr));
      });
  };

  // Shared tail of both upload paths. The records are already saved when `onSuccess` runs, so
  // a failure there is reported as such instead of surfacing as an upload/connectivity error.
  private async applyImportOutcome(outcome: {
    data: {};
    errors: ImportError[];
    added_count?: number;
    updated_count?: number;
  }): Promise<void> {
    let errors = outcome.errors;
    if (errors.length === 0) {
      const onSuccess = this.delegate().onSuccess;
      if (onSuccess) {
        try {
          errors = await onSuccess(outcome.data);
        } catch (error) {
          // logError ignores its message when given an error and drops the api layer's 'request'
          // rejection outright, so the reason is folded into a single Error that always reaches
          // Sentry attributed to this step.
          logError('', new Error(`import onSuccess failed: ${describeRejection(error)}`));
          this.state('initial');
          this.excelUploadError(
            i18n.t(
              'The file was imported, but the page could not be updated with the result. Please reload the page.'
            )()
          );
          return;
        }
      }
      this.addedCount(outcome.added_count ?? null);
      this.updatedCount(outcome.updated_count ?? null);
      this.state('imported');
    }
    if (errors.length > 0) {
      this.state('initial');
      this.excelUploadError(
        i18n.t(
          'The uploaded Excel file is invalid. Please correct the following errors and re-upload the file:'
        )()
      );
      this.errors(errors);
    }
  }
}

let importEntities = {
  name: 'import-entities',
  viewModel: ImportEntities,
  template: template,
};

ko.components.register(importEntities.name, importEntities);
